import { useCallback, useEffect, useMemo, useRef } from 'react'
import isEqual from 'lodash/isEqual'

import { useNewJackpotsGameIds } from './useNewJackpotsGameIds'
import { useJackpotsCurrenciesGameIds } from './useJackpotsCurrenciesGameIds'
import { config } from '../../../../components/Init/Init'
import { useConfigValueByCurrentDevice } from '../../../../hooks/useConfigValueByCurrentDevice'
import { isPlayerTestAccount } from '../../../utils'
import { useSetJackpotsData } from './useSetJackpotsData'
import { useJackpotsStore } from '../jackpotsStore'
import { useFirebase } from '../../../providers/FirebaseProvider'
import { usePlayerStore } from '../../../store'

export const useInitJackpotsSubscription = () => {
  const playerStore = usePlayerStore()
  const playerData = playerStore.useAllPlayerStore()
  const playerLoginUser = playerStore.use.loginUser()
  const playerStatus = playerStore.use.status()

  const { firebaseCurrentUser } = useFirebase()
  const [setJackpotsDataAction] = useSetJackpotsData()

  const { getNewJackpotsGameIds } = useNewJackpotsGameIds()
  const { getJackpotCurrenciesGameIds } = useJackpotsCurrenciesGameIds()
  const enableJackpotsFirebaseListeningByDevice = useConfigValueByCurrentDevice(config.enableJackpotsFirebaseListening)
  const enableJackpotsFirebaseListening = useMemo(
    () =>
      window.rmgBase &&
      playerLoginUser &&
      playerLoginUser.userId &&
      firebaseCurrentUser &&
      enableJackpotsFirebaseListeningByDevice,
    [enableJackpotsFirebaseListeningByDevice, firebaseCurrentUser, window.rmgBase, playerLoginUser.userId]
  )
  const jackpotsQueryIntervalMs = useConfigValueByCurrentDevice(config.jackpotsQueryIntervalMs)
  const jackpotsQueryDelayMs = useConfigValueByCurrentDevice(config.jackpotsQueryDelayMs)
  const jackpotsQueryInterval = useRef(null)
  const jackpotsQueryTimer = useRef(null)

  const setJackpotsData = useCallback(
    jackpotsData => {
      const prevJackpotsData = useJackpotsStore.getState()
      const newJackpotsData = {
        jackpots: jackpotsData,
        jackpotGameIds: getNewJackpotsGameIds(jackpotsData),
        jackpotCurrenciesGameIds: getJackpotCurrenciesGameIds(jackpotsData)
      }

      if (!isEqual(prevJackpotsData.jackpotsData.jackpots, newJackpotsData.jackpots)) {
        setJackpotsDataAction(newJackpotsData)
      }
    },
    [getJackpotCurrenciesGameIds, getNewJackpotsGameIds]
  )

  const initJackpotsFirebaseSubscription = useCallback(() => {
    const updateJackpots = () => {
      const rmgBase = window.rmgBase

      if (rmgBase) {
        const db = rmgBase.database()

        db.ref(`jackpots`)
          .once('value')
          .then(snapshot => {
            let jackpots = snapshot.val()
            if (!jackpots) {
              jackpots = {}
            }

            let jackpotsData = Object.getOwnPropertyNames(jackpots)
              .map(name => jackpots[name])
              .filter(jp => !!jp.test === isPlayerTestAccount(playerData))

            setJackpotsData(jackpotsData)
          })
          .catch(error => {
            console.log('Failed to fetch jackpots data FB user:', firebaseCurrentUser)
            console.error('Failed to fetch jackpots data:', error.message)
          })
      }
    }

    const startJackpotsFetching = () => {
      updateJackpots()
      jackpotsQueryInterval.current = setInterval(updateJackpots, jackpotsQueryIntervalMs)
    }

    jackpotsQueryTimer.current = setTimeout(() => {
      startJackpotsFetching()
    }, jackpotsQueryDelayMs || 0)
  }, [playerData, setJackpotsData])

  useEffect(() => {
    if (enableJackpotsFirebaseListening) {
      initJackpotsFirebaseSubscription()
    } else {
      clearInterval(jackpotsQueryInterval.current)
    }

    return () => {
      clearTimeout(jackpotsQueryTimer.current)
      clearInterval(jackpotsQueryInterval.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableJackpotsFirebaseListening])

  useEffect(() => {
    if (playerStatus !== config.status.partial && playerStatus !== config.status.registered) {
      clearInterval(jackpotsQueryInterval.current)
    }
  }, [playerStatus])
}
